import {BlockWithAlignableContents} from '@lexical/react/LexicalBlockWithAlignableContents';
import {
  DecoratorBlockNode,
  SerializedDecoratorBlockNode,
} from '@lexical/react/LexicalDecoratorBlockNode';
import type {
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  EditorConfig,
  ElementFormatType,
  LexicalEditor,
  LexicalNode,
  NodeKey,
  Spread,
} from 'lexical';
import {getAuraPasteAllowedHostnames, getAuraPublicOrigin} from 'utilities';

import {buildAuraEmbedSrc, resolveAuraEmbedOrigin} from './auraVideoUrl';

/**
 * Aura is only reachable on the corp network (it resolves to a private 100.64.x address), while
 * CompanyOS is served from a public address. Chrome's Local Network Access checks block a public
 * page's iframe from loading a private-network address unless the embedding page delegates the
 * permission, and show "The connection is blocked because it was initiated by a public page..."
 * `local-network-access` is the original policy name; Chrome is splitting it into
 * `local-network` / `loopback-network`, so list both. Browsers ignore names they don't know.
 */
export const AURA_IFRAME_ALLOW = [
  'accelerometer',
  'autoplay',
  'clipboard-write',
  'encrypted-media',
  'gyroscope',
  'picture-in-picture',
  'local-network-access',
  'local-network',
].join('; ');

const auraOriginFor = (origin: string | undefined) =>
  resolveAuraEmbedOrigin(origin, getAuraPasteAllowedHostnames(), getAuraPublicOrigin());

type AuraVideoComponentProps = Readonly<{
  className: Readonly<{
    base: string;
    focus: string;
  }>;
  format: ElementFormatType | null;
  nodeKey: NodeKey;
  origin: string | undefined;
  videoRef: string;
}>;

const AuraVideoComponent = ({
  className,
  format,
  nodeKey,
  origin,
  videoRef,
}: AuraVideoComponentProps) => {
  const src = buildAuraEmbedSrc(videoRef, auraOriginFor(origin));

  return (
    <BlockWithAlignableContents className={className} format={format} nodeKey={nodeKey}>
      {src ? (
        <iframe
          src={src}
          width="100%"
          height="390"
          frameBorder="0"
          allow={AURA_IFRAME_ALLOW}
          allowFullScreen
          title="Aura video"
        />
      ) : (
        <span data-aura-embed-disabled="true">
          Aura video embed unavailable (invalid reference)
        </span>
      )}
    </BlockWithAlignableContents>
  );
};

export type SerializedAuraVideoNode = Spread<
  {
    videoRef: string;
    /** Aura deployment the video lives on. Absent on embeds created before this was stored. */
    origin?: string;
  },
  SerializedDecoratorBlockNode
>;

function convertAuraVideoElement(domNode: HTMLElement): null | DOMConversionOutput {
  const videoRef = domNode.getAttribute('data-lexical-aura-video');
  if (videoRef) {
    const node = $createAuraVideoNode(
      videoRef,
      domNode.getAttribute('data-lexical-aura-origin') ?? undefined
    );
    return {node};
  }
  return null;
}

export class AuraVideoNode extends DecoratorBlockNode {
  __videoRef: string;
  __origin: string | undefined;

  static getType(): string {
    return 'aura-video';
  }

  static clone(node: AuraVideoNode): AuraVideoNode {
    return new AuraVideoNode(node.__videoRef, node.__origin, node.__format, node.__key);
  }

  static importJSON(serializedNode: SerializedAuraVideoNode): AuraVideoNode {
    const node = $createAuraVideoNode(serializedNode.videoRef, serializedNode.origin);
    node.setFormat(serializedNode.format);
    return node;
  }

  exportJSON(): SerializedAuraVideoNode {
    return {
      ...super.exportJSON(),
      type: 'aura-video',
      version: 1,
      videoRef: this.__videoRef,
      ...(this.__origin ? {origin: this.__origin} : {}),
    };
  }

  constructor(videoRef: string, origin?: string, format?: ElementFormatType, key?: NodeKey) {
    super(format, key);
    this.__videoRef = videoRef;
    this.__origin = origin;
  }

  exportDOM(): DOMExportOutput {
    const element = document.createElement('iframe');
    element.setAttribute('data-lexical-aura-video', this.__videoRef);
    if (this.__origin) {
      element.setAttribute('data-lexical-aura-origin', this.__origin);
    }
    element.setAttribute('width', '100%');
    element.setAttribute('height', '390');
    const src = buildAuraEmbedSrc(this.__videoRef, auraOriginFor(this.__origin));
    if (src) {
      element.setAttribute('src', src);
    }
    element.setAttribute('frameborder', '0');
    element.setAttribute('allow', AURA_IFRAME_ALLOW);
    element.setAttribute('allowfullscreen', 'true');
    element.setAttribute('title', 'Aura video');
    return {element};
  }

  static importDOM(): DOMConversionMap | null {
    return {
      iframe: (domNode: HTMLElement) => {
        if (!domNode.hasAttribute('data-lexical-aura-video')) {
          return null;
        }
        return {
          conversion: convertAuraVideoElement,
          priority: 1,
        };
      },
    };
  }

  updateDOM(): false {
    return false;
  }

  getVideoRef(): string {
    return this.__videoRef;
  }

  getTextContent(): string {
    return `${auraOriginFor(this.__origin)}/video/${this.__videoRef}`;
  }

  decorate(_editor: LexicalEditor, config: EditorConfig) {
    const embedBlockTheme = config.theme.embedBlock || {};
    const className = {
      base: embedBlockTheme.base || '',
      focus: embedBlockTheme.focus || '',
    };
    return (
      <AuraVideoComponent
        className={className}
        format={this.__format}
        nodeKey={this.getKey()}
        origin={this.__origin}
        videoRef={this.__videoRef}
      />
    );
  }
}

export function $createAuraVideoNode(videoRef: string, origin?: string): AuraVideoNode {
  return new AuraVideoNode(videoRef, origin);
}

export function $isAuraVideoNode(
  node: AuraVideoNode | LexicalNode | null | undefined
): node is AuraVideoNode {
  return node instanceof AuraVideoNode;
}
